import React from "react";
import { Card, Row, Col, Spin, Typography, Tag, Divider, Button, Alert } from "antd";
import { 
  BankOutlined, 
  PhoneOutlined, 
  MailOutlined, 
  EnvironmentOutlined,
  UserOutlined,
  FileTextOutlined,
  MobileOutlined,
  WhatsAppOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  FacebookOutlined,
  InstagramOutlined,
  PercentageOutlined,
  EditOutlined
} from "@ant-design/icons";

const { Text } = Typography;

function CompanyDetailCard({ companyDetail, loading }) {
  if (loading) {
    return (
      <Card className="mb-4" size="small">
        <div className="flex justify-center items-center py-4">
          <Spin />
        </div>
      </Card>
    );
  }

  if (!companyDetail) {
    return null;
  }

  const InfoItem = ({ icon, label, value, link = false, whatsapp = false }) => {
    // Always show the item, even if value is empty
    let content;
    
    if (!value || value === "") {
      content = <Text className="text-sm text-gray-400">--</Text>;
    } else if (whatsapp) {
      // Clean phone number for WhatsApp (remove spaces, dashes, parentheses)
      const cleanPhone = value.replace(/[\s\-\(\)]/g, '');
      content = (
        <a 
          href={`https://wa.me/${cleanPhone}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-green-500 hover:text-green-700"
        >
          {value}
        </a>
      );
    } else if (link) {
      content = (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
          {value}
        </a>
      );
    } else {
      content = <Text className="text-sm">{value}</Text>;
    }

    return (
      <div className="flex items-center space-x-2 mb-2">
        {icon}
        <Text strong className="text-xs text-gray-600 min-w-[80px]">{label}:</Text>
        {content}
      </div>
    );
  };

  const handleEditCompany = () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const editUrl = `${baseUrl}admin/basics/company/${companyDetail.id}/change/`;
    window.open(editUrl, '_blank');
  };

  return (
    <Card 
      className="mb-4" 
      size="small"
      title={
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <BankOutlined className="mr-2 text-blue-500" />
            <span className="text-base">Información de la Empresa</span>
          </div>
          <div className="flex items-center gap-2">
            {companyDetail.isDeprecated && (
              <Tag color="red">Deprecada</Tag>
            )}
            <Button 
              type="primary" 
              size="small" 
              icon={<EditOutlined />}
              onClick={handleEditCompany}
            >
              Editar
            </Button>
          </div>
        </div>
      }
    >
      <Row gutter={[16, 8]}>
        {/* Información básica */}
        <Col xs={24} sm={12} md={8}>
          <InfoItem 
            icon={<BankOutlined className="text-gray-500" />}
            label="Nombre"
            value={companyDetail.name}
          />
          <InfoItem 
            icon={<PhoneOutlined className="text-gray-500" />}
            label="Teléfono"
            value={companyDetail.phone}
          />
          <InfoItem 
            icon={<MobileOutlined className="text-gray-500" />}
            label="Móvil"
            value={companyDetail.mobile}
          />
          <InfoItem 
            icon={<WhatsAppOutlined className="text-green-500" />}
            label="WhatsApp"
            value={companyDetail.mobileWhastapp}
            whatsapp={true}
          />
        </Col>

        {/* Contacto y ubicación */}
        <Col xs={24} sm={12} md={8}>
          <InfoItem 
            icon={<MailOutlined className="text-gray-500" />}
            label="Email"
            value={companyDetail.email}
          />
          <InfoItem 
            icon={<EnvironmentOutlined className="text-gray-500" />}
            label="Provincia"
            value={companyDetail.province}
          />
          <InfoItem 
            icon={<GlobalOutlined className="text-gray-500" />}
            label="Web"
            value={companyDetail.web}
            link={companyDetail.web && companyDetail.web.startsWith('http')}
          />
        </Col>

        {/* Comisiones y redes sociales */}
        <Col xs={24} sm={12} md={8}>
          <InfoItem 
            icon={<PercentageOutlined className="text-green-600" />}
            label="Com. Coord."
            value={companyDetail.coordinatorCommission ? `${companyDetail.coordinatorCommission}%` : ''}
          />
          <InfoItem 
            icon={<PercentageOutlined className="text-blue-600" />}
            label="Com. Empresa"
            value={companyDetail.companyCommission ? `${companyDetail.companyCommission}%` : ''}
          />
          <InfoItem 
            icon={<LinkedinOutlined className="text-blue-700" />}
            label="LinkedIn"
            value={companyDetail.linkedIn}
            link={companyDetail.linkedIn && companyDetail.linkedIn !== ''}
          />
          <InfoItem 
            icon={<FacebookOutlined className="text-blue-600" />}
            label="Facebook"
            value={companyDetail.facebook}
            link={companyDetail.facebook && companyDetail.facebook !== ''}
          />
          <InfoItem 
            icon={<InstagramOutlined className="text-pink-500" />}
            label="Instagram"
            value={companyDetail.instagram}
            link={companyDetail.instagram && companyDetail.instagram !== ''}
          />
        </Col>

        {/* Observación */}
        {companyDetail.observation && companyDetail.observation.trim() !== '' ? (
          <Col xs={24}>
            <Divider className="my-2" />
            <Alert
              message="Observación de la Empresa"
              description={companyDetail.observation}
              type="info"
              icon={<FileTextOutlined />}
              showIcon
              className="border-green-300 bg-green-50"
              style={{
                borderColor: '#52c41a',
                backgroundColor: '#f6ffed'
              }}
            />
          </Col>
        ) : (
          <Col xs={24}>
            <Divider className="my-2" />
            <InfoItem 
              icon={<FileTextOutlined className="text-gray-500" />}
              label="Observación"
              value={companyDetail.observation}
            />
          </Col>
        )}
      </Row>
    </Card>
  );
}

export default CompanyDetailCard;
